/**
 * Telegram Channel Adapter
 *
 * Uses Telegram Bot API with long polling to receive messages
 * and send responses.
 */

import {
	BaseChannelAdapter,
	globalChannelRegistry,
	type ChannelConfig,
	type ChannelProfile,
} from "./base.js";
import type { InboundMessage, OutboundMessage, SenderInfo } from "../types/index.js";

/** Telegram channel configuration */
export interface TelegramConfig {
	/** Bot token from @BotFather */
	botToken: string;
	/** Polling interval in ms (default: 1000) */
	pollingInterval?: number;
	/** Allowed chat IDs (empty = allow all) */
	allowedChatIds?: string[];
}

/** Telegram adapter */
export class TelegramAdapter extends BaseChannelAdapter {
	readonly channelType = "telegram";

	private botToken: string;
	private pollingInterval: number;
	private allowedChatIds: string[];
	private offset = 0;
	private pollingTimeout?: NodeJS.Timeout;
	private botInfo?: TelegramUser;
	private abortController?: AbortController;

	constructor(config: ChannelConfig) {
		super(config);
		const tgConfig = config.config as unknown as TelegramConfig;
		this.botToken = tgConfig.botToken;
		this.pollingInterval = tgConfig.pollingInterval ?? 1000;
		this.allowedChatIds = tgConfig.allowedChatIds ?? [];
	}

	/** Initialize the adapter */
	async initialize(): Promise<void> {
		// Get bot info
		const response = await this.apiCall<TelegramUser>("getMe");
		if (!response.ok) {
			throw new Error(`Failed to initialize Telegram bot: ${response.description}`);
		}
		this.botInfo = response.result;
		this.connected = true;

		console.log(`Telegram bot initialized: @${this.botInfo?.username ?? 'unknown'}`);

		// Start polling
		this.startPolling();
	}

	/** Shutdown the adapter */
	async shutdown(): Promise<void> {
		this.connected = false;
		if (this.pollingTimeout) {
			clearTimeout(this.pollingTimeout);
		}
		this.abortController?.abort();
	}

	/** Send a message to Telegram */
	async send(message: OutboundMessage): Promise<void> {
		const chatId = this.extractChatId(message.target);

		for (const part of message.parts) {
			switch (part.type) {
				case "text": {
					// Chunk text if needed (Telegram max message length is 4096)
					const chunks = this.chunkText(part.text, 4096);
					for (const chunk of chunks) {
						await this.apiCall("sendMessage", {
							chat_id: chatId,
							text: chunk,
							parse_mode: "Markdown",
							reply_to_message_id: message.replyTo,
						});
					}
					break;
				}

				case "image": {
					await this.apiCall("sendPhoto", {
						chat_id: chatId,
						photo: part.url,
						caption: part.caption,
						parse_mode: "Markdown",
						reply_to_message_id: message.replyTo,
					});
					break;
				}

				case "file": {
					await this.apiCall("sendDocument", {
						chat_id: chatId,
						document: part.url,
						caption: part.name,
						reply_to_message_id: message.replyTo,
					});
					break;
				}

				case "typing": {
					await this.apiCall("sendChatAction", {
						chat_id: chatId,
						action: "typing",
					});
					break;
				}

				case "reaction": {
					// Set message reaction (if messageId is available)
					if (part.messageId) {
						await this.apiCall("setMessageReaction", {
							chat_id: chatId,
							message_id: part.messageId,
							reaction: [{ type: "emoji", emoji: part.emoji }],
						});
					}
					break;
				}
			}
		}
	}

	/** Get channel profile */
	async getProfile(): Promise<ChannelProfile> {
		return {
			type: this.channelType,
			id: this.botInfo?.id.toString() ?? "unknown",
			name: this.botInfo?.first_name ?? "Telegram Bot",
			username: this.botInfo?.username,
			connected: this.connected,
			capabilities: {
				text: true,
				images: true,
				files: true,
				reactions: true,
				threads: true,
				editMessages: true,
				deleteMessages: true,
				typing: true,
				cards: false, // Limited support via inline keyboards
				voice: true,
				maxMessageLength: 4096,
				maxFileSize: 50 * 1024 * 1024, // 50MB for bots
			},
		};
	}

	/** Start polling for updates */
	private startPolling(): void {
		if (!this.connected) return;

		this.pollingTimeout = setTimeout(async () => {
			try {
				await this.pollUpdates();
			} catch (error) {
				console.error("Telegram polling error:", error);
			}
			if (this.connected) {
				this.startPolling();
			}
		}, this.pollingInterval);
	}

	/** Poll for updates */
	private async pollUpdates(): Promise<void> {
		this.abortController = new AbortController();

		try {
			const response = await this.apiCall<TelegramUpdate[]>(
				"getUpdates",
				{
					offset: this.offset,
					limit: 100,
					timeout: 30,
				},
				this.abortController.signal,
			);

			if (response.ok && Array.isArray(response.result) && response.result.length > 0) {
				for (const update of response.result) {
					this.processUpdate(update);
					this.offset = update.update_id + 1;
				}
			}
		} catch (error) {
			if ((error as Error).name !== "AbortError") {
				throw error;
			}
		}
	}

	/** Process a Telegram update */
	private processUpdate(update: TelegramUpdate): void {
		const message = update.message || update.edited_message;
		if (!message) return;

		// Check allowlist
		const chatId = message.chat.id.toString();
		if (this.allowedChatIds.length > 0 && !this.allowedChatIds.includes(chatId)) {
			console.log(`Ignoring message from unauthorized chat: ${chatId}`);
			return;
		}

		// Determine scope
		const scope = message.chat.type === "private" ? "dm" : "group";

		// Check for mention in groups
		if (scope === "group" && this.config.access.groupPolicy === "mention") {
			const isMentioned = this.isMentioned(message);
			if (!isMentioned) {
				return; // Ignore messages without mention
			}
		}

		// Build sender info
		const sender: SenderInfo = {
			id: message.from?.id.toString() ?? "unknown",
			name: [message.from?.first_name, message.from?.last_name].filter(Boolean).join(" ") || "Unknown",
			username: message.from?.username,
			isAdmin: false, // Would need additional API call to determine
		};

		// Build content
		let content: InboundMessage["content"];

		if (message.text) {
			// Check for commands
			if (message.text.startsWith("/")) {
				const [command, ...args] = message.text.slice(1).split(" ");
				content = {
					type: "command",
					command,
					args,
				};
			} else {
				content = {
					type: "text",
					text: this.cleanMentions(message.text),
				};
			}
		} else if (message.photo) {
			// Get largest photo
			const photo = message.photo[message.photo.length - 1];
			content = {
				type: "image",
				url: photo.file_id, // Need to get file URL via getFile
				caption: message.caption,
			};
		} else if (message.document) {
			content = {
				type: "file",
				url: message.document.file_id,
				name: message.document.file_name ?? "file",
				mimeType: message.document.mime_type ?? "application/octet-stream",
				size: message.document.file_size,
			};
		} else {
			// Unsupported message type
			return;
		}

		// Get agent ID from binding
		const agentId = this.config.name; // Simplified - should resolve via router

		// Create inbound message
		const inboundMessage = this.createInboundMessage({
			id: message.message_id.toString(),
			agentId,
			scope,
			identifier: chatId,
			sender,
			content,
			replyTo: message.reply_to_message?.message_id.toString(),
			threadId: message.message_thread_id?.toString(),
		});

		this.emitMessage(inboundMessage);
	}

	/** Check if bot is mentioned in message */
	private isMentioned(message: TelegramMessage): boolean {
		if (!message.entities) return false;

		const botUsername = this.botInfo?.username?.toLowerCase();

		for (const entity of message.entities) {
			if (entity.type === "mention") {
				const mention = message.text?.slice(entity.offset, entity.offset + entity.length).toLowerCase();
				if (mention === `@${botUsername}`) {
					return true;
				}
			}
			if (entity.type === "text_mention") {
				if (entity.user?.id === this.botInfo?.id) {
					return true;
				}
			}
		}

		return false;
	}

	/** Clean mentions from message text */
	private cleanMentions(text: string): string {
		const botUsername = this.botInfo?.username;
		if (!botUsername) return text;

		// Remove @botname from the beginning of the message
		const mentionPattern = new RegExp(`^@${botUsername}\\s*`, "i");
		return text.replace(mentionPattern, "").trim();
	}

	/** Make API call to Telegram */
	private async apiCall<T = unknown>(
		method: string,
		params: Record<string, unknown> = {},
		signal?: AbortSignal,
	): Promise<TelegramResponse<T>> {
		const url = `https://api.telegram.org/bot${this.botToken}/${method}`;

		const response = await fetch(url, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(params),
			signal,
		});

		if (!response.ok) {
			throw new Error(`Telegram API error: ${response.status} ${response.statusText}`);
		}

		return response.json() as Promise<TelegramResponse<T>>;
	}

	/** Extract chat ID from target */
	private extractChatId(target: string): string {
		// Target is the chat ID for Telegram
		return target;
	}
}

/** Register with global registry */
globalChannelRegistry.register("telegram", TelegramAdapter);

/** Telegram API types */
interface TelegramUser {
	id: number;
	is_bot: boolean;
	first_name: string;
	last_name?: string;
	username?: string;
	language_code?: string;
}

interface TelegramChat {
	id: number;
	type: "private" | "group" | "supergroup" | "channel";
	title?: string;
	username?: string;
	first_name?: string;
	last_name?: string;
}

interface TelegramMessageEntity {
	type: string;
	offset: number;
	length: number;
	url?: string;
	user?: TelegramUser;
}

interface TelegramPhotoSize {
	file_id: string;
	file_unique_id: string;
	width: number;
	height: number;
	file_size?: number;
}

interface TelegramDocument {
	file_id: string;
	file_unique_id: string;
	file_name?: string;
	mime_type?: string;
	file_size?: number;
}

interface TelegramMessage {
	message_id: number;
	from?: TelegramUser;
	date: number;
	chat: TelegramChat;
	text?: string;
	caption?: string;
	entities?: TelegramMessageEntity[];
	photo?: TelegramPhotoSize[];
	document?: TelegramDocument;
	reply_to_message?: TelegramMessage;
	message_thread_id?: number;
}

interface TelegramUpdate {
	update_id: number;
	message?: TelegramMessage;
	edited_message?: TelegramMessage;
	channel_post?: TelegramMessage;
	edited_channel_post?: TelegramMessage;
}

interface TelegramResponse<T = unknown> {
	ok: boolean;
	result: T;
	description?: string;
	error_code?: number;
}
